import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import client, { endpoints, errText } from '../../api/client';
import StatusBadge from '../../components/StatusBadge';
import { money, dt, when, FREQUENCY_LABEL, titleise } from '../../lib/format';

// What the dispatcher can move this job to next. Mirrors the server's state
// machine — the server is still the authority, so a stale page gets a clear 409
// rather than a wrong result.
const NEXT = {
  awaiting_deposit: ['confirmed', 'cancelled'],
  confirmed: ['assigned', 'cancelled'],
  assigned: ['in_progress', 'confirmed', 'cancelled'],
  in_progress: ['quality_check', 'cancelled'],
  quality_check: ['completed', 'in_progress'],
  completed: [],
  cancelled: [],
};

export default function BookingDetail() {
  const { ref } = useParams();
  const [b, setB] = useState(null);
  const [teams, setTeams] = useState([]);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await client.get(endpoints.bookings.get(ref));
      setB(r.data.booking);
      const t = await client.get(endpoints.teams.list, { params: { active: '1' } });
      setTeams(t.data.items || []);
    } catch (e) { setErr(errText(e)); }
  }, [ref]);

  useEffect(() => { load(); }, [load]);

  // One wrapper for every mutation: it clears the last error, reloads on success,
  // and surfaces the API's own refusal message on failure — which is where the
  // useful text lives ("2 checklist item(s) not signed off: Bathrooms").
  const act = (fn) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); setNote(''); }
    catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  if (err && !b) return <section className="section"><p className="alert alert-err">{err}</p></section>;
  if (!b) return <section className="section"><p className="muted">Loading…</p></section>;

  const checklistDone = (b.checklist || []).filter((c) => c.done).length;

  return (
    <section className="section">
      <div className="section-head">
        <h1>{b.reference} <StatusBadge status={b.status} /></h1>
        <Link to="/ops/bookings">← All bookings</Link>
      </div>
      {err && <p className="alert alert-err">{err}</p>}

      <div className="cols">
        <div className="col">
          <div className="card">
            <h2>The job</h2>
            <dl className="kv">
              <dt>Service</dt><dd>{b.serviceName} <span className="muted">({FREQUENCY_LABEL[b.frequency] || b.frequency}{b.channel === 'b2b' ? ' · B2B' : ''})</span></dd>
              <dt>When</dt><dd>{when(b.date, b.time)} <span className="muted">{b.timezone}</span></dd>
              <dt>Where</dt><dd>{b.property?.address}{b.property?.landmark ? ` (${b.property.landmark})` : ''}, {b.city}</dd>
              <dt>Property</dt><dd>
                {titleise(b.property?.type)}
                {b.property?.bedrooms ? ` · ${b.property.bedrooms} bed` : ''}
                {b.property?.bathrooms ? ` · ${b.property.bathrooms} bath` : ''}
                {b.property?.sizeSqm ? ` · ${b.property.sizeSqm} sqm` : ''}
                {b.property?.occupied ? ' · occupied' : ' · vacant'}
              </dd>
              {b.property?.accessNotes && <><dt>Access</dt><dd>{b.property.accessNotes}</dd></>}
              {b.property?.requirements && <><dt>Requirements</dt><dd className="prewrap">{b.property.requirements}</dd></>}
              {b.quote && <><dt>From quote</dt><dd><Link to={`/ops/quotes/${b.quote.reference || b.quote}`}>{b.quote.reference || 'view'}</Link></dd></>}
            </dl>
          </div>

          <div className="card">
            <h2>Customer</h2>
            <dl className="kv">
              <dt>Name</dt><dd>{b.contact?.name}{b.contact?.company ? ` · ${b.contact.company}` : ''}</dd>
              <dt>Phone</dt><dd><a href={`tel:${b.contact?.phone}`}>{b.contact?.phone}</a></dd>
              <dt>WhatsApp</dt><dd>
                {/* The number the brief calls the primary channel — one tap from here. */}
                <a href={`https://wa.me/${String(b.contact?.whatsappSame ? b.contact?.phone : b.contact?.whatsapp || b.contact?.phone).replace(/\D/g, '')}`}
                   target="_blank" rel="noreferrer">Open chat</a>
              </dd>
              <dt>Email</dt><dd><a href={`mailto:${b.contact?.email}`}>{b.contact?.email}</a></dd>
            </dl>
          </div>

          <div className="card">
            <h2>Quality checklist <span className="muted">({checklistDone}/{(b.checklist || []).length})</span></h2>
            {(b.checklist || []).length === 0 && (
              <p className="muted">
                No checklist on this job. It was booked before this service had one —
                add items to <Link to="/ops/services">the service</Link> so future jobs carry it.
              </p>
            )}
            <ul className="checklist">
              {(b.checklist || []).map((item) => (
                <li key={item._id}>
                  <label>
                    <input type="checkbox" checked={!!item.done} disabled={busy}
                           onChange={act(() => client.put(endpoints.bookings.checklist(b.reference), { itemId: item._id, done: !item.done }))} />
                    <span className={item.done ? 'done' : ''}>{item.label}</span>
                    {!item.required && <em className="muted small"> optional</em>}
                  </label>
                  {item.done && <span className="muted small">{dt(item.doneAt)}</span>}
                </li>
              ))}
            </ul>
            <form className="inline-form" onSubmit={(e) => {
              e.preventDefault();
              const label = e.target.elements.label.value.trim();
              if (!label) return;
              act(() => client.put(endpoints.bookings.checklist(b.reference), { addLabel: label }))();
              e.target.reset();
            }}>
              <input name="label" placeholder="Add something the team found on site" />
              <button className="btn-ghost" disabled={busy}>Add</button>
            </form>
          </div>

          <div className="card">
            <h2>Photos of the work</h2>
            <div className="photos">
              {(b.photos || []).map((p) => (
                <figure key={p._id}>
                  <img src={p.url} alt={p.caption || p.kind} loading="lazy" />
                  <figcaption>{p.kind}{p.caption ? ` · ${p.caption}` : ''}
                    <button className="link-btn" disabled={busy}
                            onClick={act(() => client.delete(endpoints.bookings.photo(b.reference, p._id)))}>remove</button>
                  </figcaption>
                </figure>
              ))}
              {(b.photos || []).length === 0 && <p className="muted">None yet. Customers who are not on site see these on their tracking page.</p>}
            </div>
            <form className="inline-form" onSubmit={(e) => {
              e.preventDefault();
              if (!photoUrl) return;
              act(() => client.post(endpoints.bookings.photos(b.reference), { url: photoUrl, caption: photoCaption, kind: 'after' }))();
              setPhotoUrl(''); setPhotoCaption('');
            }}>
              <input placeholder="https://… link to the photo" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} />
              <input placeholder="caption" value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} />
              <button className="btn-ghost" disabled={busy}>Add photo</button>
            </form>
          </div>
        </div>

        <div className="col col-side">
          <div className="card">
            <h2>Move this job on</h2>
            <textarea rows={2} placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="btn-row">
              {(NEXT[b.status] || []).map((s) => (
                <button key={s} className={s === 'cancelled' ? 'btn-danger' : 'btn-primary'} disabled={busy}
                        onClick={act(() => (s === 'cancelled'
                          ? client.delete(endpoints.bookings.cancel(b.reference), { data: { note } })
                          : client.put(endpoints.bookings.status(b.reference), { status: s, note })))}>
                  {s.replace(/_/g, ' ')}
                </button>
              ))}
              {(NEXT[b.status] || []).length === 0 && <p className="muted">This job is closed.</p>}
            </div>
          </div>

          <div className="card">
            <h2>Team</h2>
            <p>{b.team?.name || <span className="muted">Not assigned</span>}
              {b.assignedAt && <span className="muted small"> · {dt(b.assignedAt)}</span>}</p>
            <select disabled={busy} value="" onChange={(e) => e.target.value && act(() => client.put(endpoints.bookings.assign(b.reference), { team: e.target.value }))()}>
              <option value="">Assign a team…</option>
              {teams.filter((t) => String(t.area?._id || t.area) === String(b.area?._id || b.area)).map((t) => (
                <option key={t._id} value={t._id}>{t.name} ({t.openJobs} open)</option>
              ))}
            </select>
            <p className="muted small">Only teams based in {b.city} are listed — dispatch is local.</p>
          </div>

          <div className="card">
            <h2>Money</h2>
            <dl className="kv">
              <dt>Total</dt><dd>{money(b.money?.totalMinor, b.money?.currency)}</dd>
              <dt>Deposit ({b.money?.depositPct}%)</dt>
              <dd className={b.money?.depositPaid ? 'paid' : 'due'}>
                {money(b.money?.depositMinor, b.money?.currency)} — {b.money?.depositPaid ? `paid ${b.money.depositMethod}` : 'due'}
              </dd>
              <dt>Balance</dt>
              <dd className={b.money?.balancePaid ? 'paid' : 'due'}>
                {money(b.money?.balanceMinor, b.money?.currency)} — {b.money?.balancePaid ? `paid ${b.money.balanceMethod}` : 'due'}
              </dd>
            </dl>
            {['deposit', 'balance'].map((part) => (
              <form key={part} className="inline-form" onSubmit={(e) => {
                e.preventDefault();
                const method = e.target.elements.method.value;
                const reference = e.target.elements.reference.value;
                act(() => client.put(endpoints.bookings.payment(b.reference), { part, method, reference }))();
              }}>
                <span className="tag">{part}</span>
                <select name="method" defaultValue="transfer">
                  <option value="transfer">transfer</option>
                  <option value="cash">cash</option>
                  <option value="card">card</option>
                  <option value="waived">waived</option>
                </select>
                <input name="reference" placeholder="bank ref" />
                <button className="btn-ghost" disabled={busy}>Mark paid</button>
              </form>
            ))}
          </div>

          <div className="card">
            <h2>Walkthrough</h2>
            <p className="muted small">
              {b.walkthrough?.required ? 'Required for this job — it cannot be signed off complete without one.' : 'Not required for this job.'}
            </p>
            {b.walkthrough?.doneAt
              ? <p>Done {dt(b.walkthrough.doneAt)} with {b.walkthrough.by}{b.walkthrough.note ? ` — ${b.walkthrough.note}` : ''}</p>
              : (
                <form className="inline-form" onSubmit={(e) => {
                  e.preventDefault();
                  const by = e.target.elements.by.value;
                  const n = e.target.elements.note.value;
                  if (!by) return;
                  act(() => client.put(endpoints.bookings.walkthrough(b.reference), { by, note: n }))();
                }}>
                  <input name="by" placeholder="who signed off" />
                  <input name="note" placeholder="note" />
                  <button className="btn-ghost" disabled={busy}>Record</button>
                </form>
              )}
          </div>

          {b.status === 'completed' && (
            <div className="card">
              <h2>Follow-up</h2>
              <p className="muted small">Due {dt(b.followUpAt)} — {b.followUpDone ? 'done' : 'outstanding'}</p>
              <div className="btn-row">
                <button className="btn-ghost" disabled={busy}
                        onClick={act(async () => {
                          const r = await client.put(endpoints.bookings.followUp(b.reference), { send: true, done: true });
                          // The API reports whether the email actually left; saying
                          // "sent" when SMTP is off would hide a silent miss.
                          if (r.data && r.data.sent === false) setErr(r.data.warning || 'Follow-up email was not delivered — reach out by WhatsApp.');
                        })}>
                  Send follow-up email
                </button>
                <button className="btn-ghost" disabled={busy}
                        onClick={act(() => client.put(endpoints.bookings.followUp(b.reference), { done: !b.followUpDone }))}>
                  Mark {b.followUpDone ? 'outstanding' : 'done'}
                </button>
              </div>
            </div>
          )}

          <div className="card">
            <h2>History</h2>
            <ol className="history">
              {(b.history || []).map((h) => (
                <li key={h._id}>
                  <strong>{h.to.replace(/_/g, ' ')}</strong> <span className="muted small">{h.by} · {dt(h.at)}</span>
                  {h.note && <div className="muted small">{h.note}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
